import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Power } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const withAuth = { withCredentials: true };
const inr = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;

const emptyForm = { code: "", type: "percent", value: "", min_order: "", expires_at: "" };

export const AdminDiscounts = () => {
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = useCallback(
    () => axios.get(`${API}/admin/discounts`, withAuth).then((r) => setCodes(r.data)).catch(() => toast.error("Could not load discount codes")),
    []
  );

  useEffect(() => {
    load();
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/discounts`,
        {
          code: form.code,
          type: form.type,
          value: Number(form.value),
          min_order: form.min_order ? Number(form.min_order) : 0,
          expires_at: form.expires_at ? new Date(`${form.expires_at}T23:59:59`).toISOString() : null,
        },
        withAuth
      );
      toast.success(`Code ${form.code.toUpperCase()} created`);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create code");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (code) => {
    try {
      await axios.patch(`${API}/admin/discounts/${code}/toggle`, {}, withAuth);
      load();
    } catch (err) {
      toast.error("Could not update code");
    }
  };

  const remove = async (code) => {
    try {
      await axios.delete(`${API}/admin/discounts/${code}`, withAuth);
      toast.success(`Code ${code} deleted`);
      load();
    } catch (err) {
      toast.error("Could not delete code");
    }
  };

  return (
    <div className="mt-12" data-testid="admin-discounts-section">
      <form onSubmit={create} className="border border-black/10 bg-white/40 p-6 md:p-8" data-testid="discount-create-form">
        <p className="overline-label mb-6">Create Discount Code</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
          <input
            required
            data-testid="discount-form-code"
            className="input-minimal uppercase"
            placeholder="Code (e.g. WELCOME10)"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })}
          />
          <select data-testid="discount-form-type" className="input-minimal bg-transparent" value={form.type} onChange={set("type")}>
            <option value="percent">Percentage off (%)</option>
            <option value="flat">Flat amount off (₹)</option>
          </select>
          <input
            required
            type="number"
            min="1"
            max={form.type === "percent" ? 90 : 100000}
            data-testid="discount-form-value"
            className="input-minimal"
            placeholder={form.type === "percent" ? "Percent (e.g. 10)" : "Amount ₹ (e.g. 200)"}
            value={form.value}
            onChange={set("value")}
          />
          <input
            type="number"
            min="0"
            data-testid="discount-form-min-order"
            className="input-minimal"
            placeholder="Min order ₹ (optional)"
            value={form.min_order}
            onChange={set("min_order")}
          />
          <input
            type="date"
            data-testid="discount-form-expiry"
            className="input-minimal"
            title="Expiry date (optional)"
            value={form.expires_at}
            onChange={set("expires_at")}
          />
        </div>
        <button type="submit" disabled={saving} data-testid="discount-form-submit" className="btn-ink mt-8 disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} strokeWidth={1.5} />}
          {saving ? "Creating…" : "Create Code"}
        </button>
      </form>

      <div className="mt-10">
        {codes.length === 0 ? (
          <p className="text-black/50">No discount codes yet.</p>
        ) : (
          <div className="border border-black/10 divide-y divide-black/10" data-testid="discount-codes-list">
            {codes.map((c) => (
              <div key={c.code} className="flex flex-wrap items-center gap-4 px-5 py-4 text-sm" data-testid={`discount-row-${c.code}`}>
                <span className={`font-medium tracking-[0.1em] ${c.active ? "" : "line-through text-black/40"}`}>{c.code}</span>
                <span className="text-[#BA8D53] font-medium">{c.type === "percent" ? `${c.value}% off` : `${inr(c.value)} off`}</span>
                <span className="text-black/45 text-xs">
                  {c.min_order > 0 ? `Min ${inr(c.min_order)}` : "No minimum"}
                  {c.expires_at ? ` · Expires ${new Date(c.expires_at).toLocaleDateString("en-IN")}` : " · No expiry"}
                  {` · Used ${c.uses || 0}×`}
                </span>
                <span className={`px-2 py-0.5 text-[0.58rem] uppercase tracking-[0.18em] ${c.active ? "bg-[#BA8D53]/15 text-[#BA8D53]" : "bg-black/10 text-black/50"}`}>
                  {c.active ? "Active" : "Disabled"}
                </span>
                <div className="ml-auto flex items-center gap-3">
                  <button
                    onClick={() => toggle(c.code)}
                    data-testid={`discount-toggle-${c.code}`}
                    title={c.active ? "Disable" : "Enable"}
                    className="text-black/40 hover:text-black transition-colors"
                  >
                    <Power size={15} strokeWidth={1.5} />
                  </button>
                  <button
                    onClick={() => remove(c.code)}
                    data-testid={`discount-delete-${c.code}`}
                    title="Delete"
                    className="text-black/40 hover:text-red-700 transition-colors"
                  >
                    <Trash2 size={15} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
